import React from "react";
import { Affix, Button, Checkbox, Group, Menu } from "@mantine/core";
import { Middleware } from "polymatic";

import { SignalMiddleware } from "@piqnt/runtime-core/signals";

import { CoreContext } from "@piqnt/runtime-core/context";
import { on, use } from "@piqnt/runtime-core/decorators";
import { UiProvider } from "@piqnt/runtime-core/react/Provider";
import { Edit, PauseIcon, PlayIcon } from "@piqnt/runtime-core/react/Icon";
import { useMiddleware, ReactMountMiddleware } from "@piqnt/runtime-core/react/ReactMount";

import { PlayConfig } from "@piqnt/runtime-core/playbook";

import { toolsList } from "./list-tools";

import { TbTools as ToolsIcon } from "react-icons/tb";
import { MdTune as SettingsIcon } from "react-icons/md";
import { BiFolder as ModuleIcon } from "react-icons/bi";

export class ToolbarPlugin extends Middleware<CoreContext> {
  constructor(public demos: PlayConfig[]) {
    super();
  }
  @use()
  signal = new SignalMiddleware();
  @use()
  reactMount = new ReactMountMiddleware({
    container: "playbook-toolbar",
    render: () => (
      <UiProvider>
        <ToolbarComponent plays={this.demos} />
      </UiProvider>
    ),
  });
  @on("activate")
  handleActivate = () => {
    this.signal.effect(() => {
      const containerElement = this.context.containerElement?.value;
      if (containerElement) {
        containerElement.style.top = "46px";
      }
      return () => {
        containerElement.style.right = "0px";
      };
    });
  };
}

interface ToolbarComponentProps {
  plays: PlayConfig[];
}

const ToolbarComponent: React.FC<ToolbarComponentProps> = (props) => {
  const { context } = useMiddleware<CoreContext>();

  const handlePlayBtnClick = React.useCallback(() => {
    if (!context) return;
    context.activity.value = "mode:play";
    context.paused.value = false;
    context.editable.value = false;
  }, [context]);

  const handlePauseBtnClick = React.useCallback(() => {
    if (!context) return;
    context.activity.value = "mode:play";
    context.paused.value = true;
    context.editable.value = false;
  }, [context]);

  const handleEditBtnClick = React.useCallback(() => {
    if (!context) return;
    context.activity.value = "mode:edit";
    context.paused.value = true;
    context.editable.value = true;
  }, [context]);

  const handleModuleChange = React.useCallback((demoUrl: string) => {
    if (!demoUrl) return;
    window.location.href = demoUrl;
  }, []);

  const handleToggleOutline = React.useCallback(() => {
    if (!context || !context.showOutline) return;
    const current = context.showOutline.value;
    context.showOutline.value = !current;
  }, [context]);

  return (
    <Affix
      style={{ borderBottom: "1px solid #333", height: "46px", background: "#222" }}
      position={{ top: 0, left: 0, right: 0 }}
      p={10}
    >
      <Group gap={12}>
        <Menu position="bottom-start">
          <Menu.Target>
            <Button title="Tools" variant="toolbar">
              <ModuleIcon />
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            {props.plays.map((play) => (
              <Menu.Item key={play.key} onClick={() => handleModuleChange(play.url)}>
                {play.key}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
        <Button
          onClick={handlePlayBtnClick}
          variant="toolbar"
          disabled={context.activity?.value === "mode:play" && !context.paused?.value}
        >
          <PlayIcon />
        </Button>
        <Button
          onClick={handlePauseBtnClick}
          variant="toolbar"
          disabled={context.activity?.value !== "mode:play" || context.paused?.value}
        >
          <PauseIcon />
        </Button>
        <Button onClick={handleEditBtnClick} variant="toolbar" disabled={context.activity?.value === "mode:edit"}>
          <Edit />
        </Button>
        <Menu position="bottom-start">
          <Menu.Target>
            <Button title="Tools" variant="toolbar">
              <ToolsIcon />
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            {toolsList.map((tool) => (
              <Menu.Item key={tool.name} onClick={() => tool.activate(context)}>
                {tool.name}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
        <Menu position="bottom-start">
          <Menu.Target>
            <Button title="Settings" variant="toolbar">
              <SettingsIcon />
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item closeMenuOnClick={false}>
              <Checkbox
                label="Show Outline"
                checked={context.showOutline?.value}
                onChange={handleToggleOutline}
                variant="toolbar"
              />
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
        <span>
          {context.activity?.value ?? "∅"}
          &nbsp;&middot;&nbsp;
          {boolean2str(context.paused?.value, "paused", "not-paused", "∅")}
          &nbsp;&middot;&nbsp;
          {boolean2str(context.editable?.value, "editable", "not-editable", "∅")}
          &nbsp;&middot;&nbsp;
          {context.activeTool?.value?.name ?? "∅"}
        </span>
      </Group>
    </Affix>
  );
};

const boolean2str = (boolean: boolean | undefined, yes: string, no: string, unset: string) => {
  return typeof boolean === "undefined" ? unset : boolean ? yes : no;
};
